import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Card } from '@xing-com/card';
import type { CompanyData } from '@xing-com/crate-jobs-helpers';
import { mediaConfined } from '@xing-com/layout-tokens';
import { Link } from '@xing-com/link';
import { ProfileImage } from '@xing-com/profile-image';
import { ProfileInfo } from '@xing-com/profile-info';
import { spaceXL, xdlColorText } from '@xing-com/tokens';
import { BodyCopy, Headline, Meta } from '@xing-com/typography';

import { EmployerActions } from './employer-actions';

type CompanyCardProps = {
  company: CompanyData;
  isAlwaysVertical: boolean;
  jobId: string;
  className?: string;
};

const StyledCard = styled(Card)`
  padding: ${spaceXL};
`;

export const StyledLink = styled(Link)`
  color: ${xdlColorText};

  &:hover {
    text-decoration-thickness: from-font;
  }
`;

// Set the alignment via CSS and not via useBreakpoints to work in SSR
const StyledProfileInfo = styled(ProfileInfo)<{
  $isAlwaysVertical: boolean;
}>`
  flex-direction: column;

  @media ${mediaConfined} {
    flex-direction: ${({ $isAlwaysVertical }) =>
      $isAlwaysVertical ? 'column' : 'row'};
  }
`;

export const CompanyCard: React.FC<CompanyCardProps> = ({
  company: {
    id,
    link,
    name,
    logo,
    companySize,
    city,
    country,
    industry,
    isFollowing,
  },
  isAlwaysVertical = false,
  jobId,
  className,
}) => {
  const { formatMessage } = useIntl();
  const linkLabel = formatMessage({
    id: 'JOBS_JOB_DETAILS_COMPANY_MODULE_CARD_LINK_LABEL',
  });
  const profileImg = (
    <ProfileImage
      alt="company logo"
      profileName={name}
      size="medium"
      src={logo}
      type="company"
    />
  );

  return (
    <StyledCard
      className={className}
      data-testid="company-card"
      to={link}
      linkProps={{
        'aria-label': link ? linkLabel : undefined,
      }}
    >
      <StyledProfileInfo
        noSpacing
        $isAlwaysVertical={isAlwaysVertical}
        profileImage={
          link ? (
            <StyledLink to={link} aria-label={linkLabel}>
              {profileImg}
            </StyledLink>
          ) : (
            profileImg
          )
        }
        actions={
          <EmployerActions
            companyId={id}
            isFollowing={isFollowing}
            jobId={jobId}
            link={link}
          />
        }
      >
        <Headline size="large">{name}</Headline>
        {industry ? <BodyCopy size="medium">{industry}</BodyCopy> : null}
        {companySize ? <BodyCopy size="medium">{companySize}</BodyCopy> : null}
        <Meta size="small" data-testid="company-card-location">
          {city}
          {country ? `${city ? ', ' : ''}${country}` : null}
        </Meta>
      </StyledProfileInfo>
    </StyledCard>
  );
};
